import { computed, action } from "mobx";
import BaseStore from "./base";
import Document from "@app/state/model/document";
import http from "@app/lib/http";
import notify from "@app/components/notify";
import { events } from "@app/lib/store";
import { ws } from "@app/lib/socket";

import appStore from "@app/state/store/application";
import reportStore from "@app/state/store/report";

const errorMessage =
    "An error occurred while preparing your response. Please try again or contact support for assistance.";

export class AIChatStore extends BaseStore {
    initializing = false;
    currentRequest = null;
    currentPromptQuestion = null;
    currentPromptContext = null;
    // Completions that arrived before prompt() registered the pending message
    pendingCompletions = new Map();

    /**
     * Returns an object representing the observable chat state.
     *
     * @returns {{
     *   opened: boolean,
     *   expanded: boolean,
     *   chatsLoaded: boolean,
     *   activeChat: any,
     *   chats: any[],
     *   messages: any[],
     *   loadingChat: boolean,
     *   loadingPrompt: boolean,
     *   loadingFeedback: boolean,
     *   sourceDocuments: any[],
     *   activeDocument: ({
     *     id: string,
     *     type: string,
     *     name: string,
     *     hasFile: boolean
     *   } | null),
     *   isActiveContextEnabled: boolean
     * }}
     */
    observable() {
        return {
            opened: false,
            expanded: true,
            collapsedWidth: parseInt(
                sessionStorage.getItem("ai-chat-collapsed-width") || "550",
                10,
            ),
            collapsedRight: parseInt(sessionStorage.getItem("ai-chat-collapsed-right") || "4", 10),
            chatsLoaded: false,
            activeChat: null,
            // Id of the in-flight message; drives `busy`, so it must be observable
            pendingMessageId: null,
            chats: [],
            messages: [],
            loadingChat: false,
            loadingPrompt: false,
            loadingFeedback: false,
            sourceDocuments: [],
            activeDocument: null,
            cancelled: false,
            draftQuestion: "",
            draftFiles: [],
            isActiveContextEnabled: true,
        };
    }

    constructor() {
        super();

        events.on("project.unload", () => {
            this.reset();
        });

        ws.on("ai.chat.update", (data) => {
            this.updateChatMessage(data.message, data.chat);

            if (this.currentPromptQuestion) {
                this.currentPromptQuestion = null;
                this.currentPromptContext = null;
            }
        });
    }

    @computed get projectId() {
        return reportStore.id;
    }

    @computed get enabled() {
        return appStore.enableAI && this.projectId;
    }

    @computed get dueEnabled() {
        return !!reportStore.config?.slr?.enabled;
    }

    @computed get sotaEnabled() {
        return !!reportStore.config?.sota?.enabled;
    }

    @computed get busy() {
        let processing = false;
        if (this.messages.length) {
            const lastMessage = this.messages[this.messages.length - 1];
            processing = ["indexing", "processing"].includes(lastMessage.status);
        }

        return this.loadingPrompt || this.loadingChat || processing || !!this.pendingMessageId;
    }

    /**
     * Show the chat modal
     */
    show() {
        if (this.opened) {
            return;
        }

        this.opened = true;
        this.loadChats();
    }

    /**
     * Hide the chat modal
     */
    hide() {
        this.opened = false;
    }

    /**
     * Show the chat modal
     */
    toggle() {
        if (this.opened) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Add new chat
     */
    @action
    async newChat() {
        this.activeChat = null;
        this.messages = [];
    }

    /**
     * Load the chat history
     */
    @action
    async loadChats() {
        if (!this.projectId || this.initializing || this.chatsLoaded) {
            return;
        }

        this.initializing = true;

        try {
            let { data } = await http.get(`/project/${this.projectId}/ai/chats`);
            this.chats = data ?? [];
        } catch (ex) {
            notify.error(errorMessage);
        }

        this.chatsLoaded = true;
        this.initializing = false;
    }

    /**
     * Remove a chat
     */
    @action
    async removeChat(chatId) {
        try {
            await http.delete(`/project/${this.projectId}/ai/chat/${chatId}`);
            this.chats = this.chats.filter((chat) => chat._id !== chatId);

            if (chatId === this.activeChat) {
                this.activeChat = null;
                this.messages = [];
            }
        } catch (ex) {
            notify.error(errorMessage);
        }
    }

    /**
     * Rename a chat
     */
    @action
    async renameChat(chatId, name) {
        try {
            await http.post(`/project/${this.projectId}/ai/chat/${chatId}/rename`, {
                name,
            });

            this.chats.map((chat) => {
                if (chat._id === chatId) {
                    chat.name = name;
                }
            });
        } catch (ex) {
            notify.error(errorMessage);
        }
    }

    /**
     * Load a chat
     */
    @action
    async loadChat(chatId) {
        if (this.activeChat === chatId) {
            return;
        }

        this.loadingChat = true;
        this.activeChat = chatId;

        try {
            let { data } = await http.get(`/project/${this.projectId}/ai/chat/${chatId}/messages`);
            this.messages = data ?? [];
        } catch (ex) {
            notify.error(errorMessage);
        }

        this.loadingChat = false;
    }

    /**
     * Set the draft question (persisted in store)
     */
    @action
    setDraftQuestion(question) {
        this.draftQuestion = question;
    }

    /**
     * Set the collapsed width and persist to sessionStorage
     */
    @action
    setCollapsedWidth(width) {
        this.collapsedWidth = width;
        sessionStorage.setItem("ai-chat-collapsed-width", String(width));
    }

    @action
    setCollapsedRight(right) {
        this.collapsedRight = right;
        sessionStorage.setItem("ai-chat-collapsed-right", String(right));
    }

    /**
     * Set the draft files (persisted in store)
     */
    @action
    setDraftFiles(files) {
        this.draftFiles = files;
    }
    /**
     * Refresh messages for the active chat (for polling progress)
     */
    @action
    async refreshMessages() {
        if (!this.activeChat) {
            return null;
        }

        try {
            let { data } = await http.get(
                `/project/${this.projectId}/ai/chat/${this.activeChat}/messages`,
            );
            this.messages = data ?? [];
            return data;
        } catch (ex) {
            // Silent fail for polling - don't show notification
            console.error("Error refreshing messages:", ex);
            return null;
        }
    }

    /**
     * Send a prompt to the backend
     */
    @action
    async prompt(prompt) {
        try {
            this.loadingPrompt = true;
            this.cancelled = false;
            this.pendingCompletions.clear();

            this.currentPromptQuestion = prompt.question;
            this.currentPromptContext = prompt.context;

            this.draftQuestion = "";
            this.draftFiles = [];

            // create new chat
            if (!this.activeChat) {
                let { data } = await http.post(`/project/${this.projectId}/ai/chat`, {
                    prompt: prompt.question,
                });

                this.activeChat = data._id;
                this.chats = [data, ...this.chats];
            }

            this.currentRequest = http.post(
                `/project/${this.projectId}/ai/chat/${this.activeChat}/prompt`,
                {
                    ...prompt,
                },
            );

            let { data } = await this.currentRequest;

            if (this.currentRequest?.canceled) {
                return;
            }

            this.pendingMessageId = data._id;

            // update the chat's updatedOn
            const chat = this.chats.find((chat) => chat._id === this.activeChat);
            if (chat) {
                chat.updatedOn = new Date();
            }

            this.chats = [...this.chats];
            this.messages = [...this.messages, data];

            // Reconcile a completion that arrived before the pending message was registered
            const earlyCompletion = this.pendingCompletions.get(String(data._id));
            if (earlyCompletion) {
                this.pendingCompletions.delete(String(data._id));
                this._applyMessageUpdate(earlyCompletion, true);
            }
        } catch (ex) {
            if (!this.cancelled) {
                notify.error(errorMessage);
            } else {
                if (this.currentPromptQuestion) {
                    this.draftQuestion = this.currentPromptQuestion;
                    this.draftFiles = this.currentPromptContext || [];
                }
            }
            this.currentPromptQuestion = null;
            this.currentPromptContext = null;
        } finally {
            this.loadingPrompt = false;
            this.currentRequest = null;
        }
    }

    /**
     * Cancel the current prompt request
     */
    @action
    async cancelPrompt() {
        this.cancelled = true;

        if (this.currentRequest) {
            this.currentRequest.cancel();
            this.currentRequest = null;
        }

        if (this.pendingMessageId && this.activeChat) {
            try {
                await http.post(
                    `/project/${this.projectId}/ai/chat/${this.activeChat}/cancel/${this.pendingMessageId}`,
                );
            } catch (ex) {
                notify.error("Error cancelling the prompt");
            }
        }

        if (this.pendingMessageId) {
            this.pendingMessageId = null;
        }

        if (this.currentPromptQuestion) {
            this.currentPromptQuestion = null;
            this.currentPromptContext = null;
        }

        this.loadingPrompt = false;
    }

    /**
     * Update the message details with the ones sent from the backend. The busy
     * state is cleared regardless of the active chat; the visible message list is
     * only touched when the update targets the active chat.
     */
    @action
    updateChatMessage(message, chatId) {
        const isActiveChat = chatId == null || String(chatId) === String(this.activeChat);
        const applied = this._applyMessageUpdate(message, isActiveChat);

        // Buffer completions seen before prompt() registers the message
        if (
            !applied &&
            this.loadingPrompt &&
            ["success", "error", "cancelled"].includes(message.status)
        ) {
            this.pendingCompletions.set(String(message._id), message);
        }
    }

    /**
     * Clear the pending/busy flag when the tracked message reaches a terminal
     * state and refresh it in the active chat's list. Returns true if it matched.
     */
    @action
    _applyMessageUpdate(message, isActiveChat) {
        let applied = false;

        if (
            this.pendingMessageId === message._id &&
            ["success", "error", "cancelled"].includes(message.status)
        ) {
            this.pendingMessageId = null;
            applied = true;
        }

        if (isActiveChat) {
            let matched = false;
            this.messages.forEach((current) => {
                if (current._id === message._id) {
                    current.status = message.status;
                    current.answer = message.answer;
                    matched = true;
                }
            });

            // New array reference so identity-based memos (combined references) recompute
            if (matched) {
                this.messages = [...this.messages];
                applied = true;
            }
        }

        return applied;
    }

    /**
     * Load the list of source documents from the backend
     */
    async loadSourceDocs() {
        let { data } = await http.get(`/project/${this.projectId}/documents`);
        const documents = data.list
            .map((entry) => {
                return new Document(entry);
            })
            .sort((a, b) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });

        this.sourceDocuments = documents;
        return documents;
    }

    /**
     * Load the list of literature articles of a given category (slr/sota)
     */
    async loadLiteratureArticles(type) {
        let { data } = await http.get(`/project/${this.projectId}/articles/literature`, { type });
        return data.list ?? [];
    }

    /**
     * Toggle the thumbs up flag
     */
    @action
    async thumbsUp(messageId) {
        try {
            if (!this.activeChat || this.loadingFeedback) {
                return;
            }

            this.loadingFeedback = true;
            await http.post(
                `/project/${this.projectId}/ai/chat/${this.activeChat}/${messageId}/thumbsUp`,
            );

            this.messages.map((question) => {
                if (question._id === messageId && question.answer) {
                    question.answer.thumbsUp = !question.answer.thumbsUp;
                    question.answer.thumbsDown = false;
                }
            });

            this.chats = [...this.chats];
            this.messages = [...this.messages];
        } catch (ex) {
            notify.error("Error submitting your feedback");
        } finally {
            this.loadingFeedback = false;
        }
    }

    /**
     * Toggle the thumbs down flag
     */
    @action
    async thumbsDown(messageId) {
        try {
            if (!this.activeChat || this.loadingFeedback) {
                return;
            }

            this.loadingFeedback = true;
            await http.post(
                `/project/${this.projectId}/ai/chat/${this.activeChat}/${messageId}/thumbsDown`,
            );

            this.messages.map((question) => {
                if (question._id === messageId && question.answer) {
                    question.answer.thumbsDown = !question.answer.thumbsDown;
                    question.answer.thumbsUp = false;
                }
            });

            this.chats = [...this.chats];
            this.messages = [...this.messages];
        } catch (ex) {
            notify.error("Error submitting your feedback");
        } finally {
            this.loadingFeedback = false;
        }
    }

    /**
     * Sets the currently active document.
     *
     * @param {{ id: string, type: string, name: string, hasFile: boolean }} doc - The document to set as active.
     */
    @action
    setActiveDocument(doc) {
        this.activeDocument = doc;
        this.isActiveContextEnabled = true;
    }

    /**
     * Enables the active document as context for prompts
     */
    @action
    enableActiveContext() {
        this.isActiveContextEnabled = true;
    }

    /**
     * Disables the active document as context for prompts
     */
    @action
    disableActiveContext() {
        this.isActiveContextEnabled = false;
    }

    /**
     * Updates the hasFile status of the active document
     *
     * @param {boolean} hasFile - The new hasFile status
     */
    @action
    updateActiveDocumentFileStatus(hasFile) {
        if (this.activeDocument) {
            this.activeDocument.hasFile = hasFile;
        }
    }

    @action
    unload() {
        this.reset();

        this.initializing = false;
    }
}

export default new AIChatStore();
